import { apiJson } from "./client";
import type { Pixel } from "../types";

export type Artwork = Pixel & { image: string };

export function normalizeArtworks(rows: unknown[]): Artwork[] {
  const seen = new Set<number>();
  return rows.flatMap((raw) => {
    if (!raw || typeof raw !== "object") return [];
    const row = raw as Artwork;
    const pixel = Number(row.pixel);
    if (!Number.isSafeInteger(pixel) || pixel <= 0 || seen.has(pixel) || typeof row.image !== "string" || !row.image.trim()) return [];
    const lat = Number(row.info?.lat), lng = Number(row.info?.lng);
    if (!Number.isFinite(lat) || !Number.isFinite(lng) || Math.abs(lat) > 90 || Math.abs(lng) > 180) return [];
    seen.add(pixel);
    return [{ pixel, image: row.image.trim(), info: { lat, lng },
      frameName: typeof row.frameName === "string" ? row.frameName : "",
      artistName: typeof row.artistName === "string" ? row.artistName : "",
      location: typeof row.location === "string" ? row.location : "",
      country: typeof row.country === "string" ? row.country : "" }];
  });
}

export async function fetchArtworks(signal?: AbortSignal): Promise<Artwork[]> {
  const data = await apiJson<{ artworks?: unknown[] }>("/pixel?artworks=1", { signal });
  if (!Array.isArray(data.artworks)) throw new Error("ARTWORK_ENDPOINT_UNAVAILABLE");
  return normalizeArtworks(data.artworks);
}

export function searchArtworks(rows: Artwork[], query: string): Artwork[] {
  const term = query.trim().normalize("NFKC").toLocaleLowerCase();
  return rows.filter(row => [row.frameName, row.artistName].some(value => (value || "").normalize("NFKC").toLocaleLowerCase().includes(term)));
}

export function randomArtworks(rows: Artwork[], count = 9): Artwork[] {
  const shuffled = [...rows];
  for (let i = shuffled.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [shuffled[i], shuffled[j]] = [shuffled[j], shuffled[i]];
  }
  return shuffled.slice(0, count);
}
